/* ─────────────────────────────────────────────────────────────────────────
   Call-flow node taxonomy.
   Each entry drives:
     • The left palette (drag source)
     • The canvas renderer (custom CallFlowNode component)
     • The right-side config drawer (which fields show up)
   Voicemail was intentionally removed — Voxinity's philosophy is to never
   miss a call, so callers always reach an AI agent (with no_answer fallback
   for transfers).
   ───────────────────────────────────────────────────────────────────────── */

import {
  PhoneCall, Menu as MenuIcon, Clock, Tag, User, Bot, PhoneForwarded,
  PhoneOff, Hourglass, Building2,
} from 'lucide-react';

export const HOLD_MUSIC = [
  { id: 'calm_piano', label: 'Calm piano', url: 'https://s3.amazonaws.com/com.twilio.music.classical/ith_chopin-15-2.mp3' },
  { id: 'corporate_light', label: 'Corporate light', url: 'https://s3.amazonaws.com/com.twilio.music.electronica/teru_-_110_Downtempo_Electronic_4.mp3' },
  { id: 'acoustic', label: 'Acoustic', url: 'https://s3.amazonaws.com/com.twilio.music.guitars/Pitx_-_Long_Winter.mp3' },
  { id: 'ambient', label: 'Ambient', url: 'https://s3.amazonaws.com/com.twilio.music.ambient/aerosolspray_-_Living_Taciturn.mp3' },
  { id: 'classical', label: 'Classical strings', url: 'https://s3.amazonaws.com/com.twilio.music.classical/BusyStrings.mp3' },
  { id: 'orchestral', label: 'Orchestral', url: 'https://s3.amazonaws.com/com.twilio.sounds.music/MARKOVICHAMP-Borghestral.mp3' },
];

export const NODE_CATEGORIES = [
  {
    id: 'entry',
    label: 'ENTRY',
    items: [
      { type: 'start', label: 'Start', icon: PhoneCall, color: 'emerald',
        description: 'Inbound call enters the flow here.' },
    ],
  },
  {
    id: 'routing',
    label: 'ROUTING',
    items: [
      { type: 'menu', label: 'Menu', icon: MenuIcon, color: 'sky',
        description: 'Say or press options to branch.' },
      { type: 'queue', label: 'Hold Queue', icon: Hourglass, color: 'amber',
        description: 'Hold music, then on to a department.' },
      { type: 'department', label: 'Department', icon: Building2, color: 'teal',
        description: 'Ring people, AI agent numbers or both. Adds its hold queue.' },
      { type: 'tag_filter', label: 'Tag Filter', icon: Tag, color: 'sky',
        description: 'Branch by LeadConnector contact tag.' },
      { type: 'contact_filter', label: 'Contact Filter', icon: User, color: 'sky',
        description: 'Known vs unknown contact.' },
    ],
  },
  {
    id: 'actions',
    label: 'ACTIONS',
    items: [
      { type: 'hangup', label: 'Hangup', icon: PhoneOff, color: 'rose',
        description: 'End the call.' },
    ],
  },
  // Legacy steps: not in the palette, auto-converted to Departments on open.
  { id: 'legacy', label: 'LEGACY', hidden: true, items: [
      { type: 'time_router', label: 'Time Router', icon: Clock, color: 'sky', description: '' },
      { type: 'ai_agent', label: 'AI Agent', icon: Bot, color: 'teal', description: '' },
      { type: 'transfer', label: 'Transfer', icon: PhoneForwarded, color: 'teal', description: '' },
  ] },
];

// Defaults applied when a fresh node is dropped on the canvas.
// `start` carries no user-editable data — it just shows the inbound number
// the flow is bound to (which lives on the parent flow doc, not the node).
export const NODE_DEFAULTS = {
  start:          { ai_cap: null },
  menu:           { prompt: 'Press 1 for sales, 2 for support. Press 9 to repeat this menu.', timeout: 10,
                    options: [{ key: '1', label: 'Option 1', handle_id: 'key_1' },
                              { key: '2', label: 'Option 2', handle_id: 'key_2' }],
                    // Reserved navigation keys — handled inline by the
                    // runtime, no extra output handles required.
                    repeat_enabled: true,  repeat_key: '9',
                    main_menu_enabled: false, main_menu_key: '0' },
  time_router:    { timezone: 'America/New_York',
                    windows: [{ days: ['mon','tue','wed','thu','fri'], start: '09:00', end: '17:00' }] },
  tag_filter:     { tag: '' },
  contact_filter: {},
  ai_agent:       { agent_id: '', max_duration_seconds: 600 },
  // ring_timeout: how long to ring before falling through the no_answer
  // branch. Default 20s matches Twilio's default Dial timeout.
  transfer:       { number: '', ring_timeout: 20 },
  hangup:         { farewell: '' },
  queue:          { hold_music: 'calm_piano', min_hold_sec: 0, announce_position: true, announce_wait: true, callback_offer: true, callback_after_min: 0, max_wait_min: 20 },
  department:     { name: '', numbers: [{ number: '', label: '' }], strategy: 'simultaneous', ring_seconds: 20, announce: true,
                    announce_text: '', hours_enabled: false, windows: [{ days: ['mon', 'tue', 'wed', 'thu', 'fri'], start: '09:00', end: '17:00' }],
                    timezone: 'America/New_York', holidays: [], record: false },
};

export const COLOR_CLASSES = {
  emerald: { bg: 'bg-emerald-400/15', border: 'border-emerald-400/40', text: 'text-emerald-300', dot: 'bg-emerald-400' },
  sky:     { bg: 'bg-sky-400/15',     border: 'border-sky-400/40',     text: 'text-sky-300',     dot: 'bg-sky-400' },
  teal:    { bg: 'bg-teal-400/15',    border: 'border-teal-400/40',    text: 'text-teal-300',    dot: 'bg-teal-400' },
  amber:   { bg: 'bg-amber-400/15',   border: 'border-amber-400/40',   text: 'text-amber-300',   dot: 'bg-amber-400' },
  rose:    { bg: 'bg-rose-400/15',    border: 'border-rose-400/40',    text: 'text-rose-300',    dot: 'bg-rose-400' },
};

// Lookup: type → meta
export const NODE_META = NODE_CATEGORIES.flatMap(c => c.items).reduce((acc, it) => {
  acc[it.type] = it;
  return acc;
}, {});

// Output handles for routing nodes (so edges land on the right port).
export const NODE_HANDLES = {
  start:          [{ id: 'out', label: 'Next' }],
  menu:           ({ data }) => [
    ...((data?.options || []).map(o => ({ id: o.handle_id || `key_${o.key}`, label: `Press ${o.key}` }))),
    { id: 'timeout', label: 'No input' },
  ],
  time_router:    [{ id: 'open', label: 'Open hours' }, { id: 'closed', label: 'Closed' }],
  tag_filter:     [{ id: 'has', label: 'Has tag' }, { id: 'lacks', label: "Doesn't have tag" }],
  contact_filter: [{ id: 'known', label: 'Known' }, { id: 'unknown', label: 'Unknown' }],
  ai_agent:       [],
  // Transfer rings the external number; if nobody answers within
  // `ring_timeout` seconds the call falls through the `no_answer` branch
  // — typically connected to an AI Agent so the call is never missed.
  transfer:       [{ id: 'no_answer', label: 'No answer / busy' }],
  hangup:         [],
  queue:          [{ id: 'out', label: 'Line free' }, { id: 'timeout', label: 'Max wait' }],
  department:     [{ id: 'no_answer', label: 'No answer' }, { id: 'closed', label: 'Closed' }],
};

// Node width grows with the number of output branches so labels never collide.
export function nodeWidth(type, data) {
  const def = NODE_HANDLES[type];
  const n = (typeof def === 'function' ? def({ data }) : def || []).length;
  return Math.max(240, n * 92 + 24);
}
